import { ChangeEvent, FC, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import QuestionTypes from 'types/QuestionTypes';
// Models
import IQuiz from 'models/Quiz';
// Redux
import { useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuiz, selectPostQuizQuestions } from 'store/quiz/quizSelectors';
// Components
import { Button } from 'components/Buttons';
import { Input, Checkbox } from 'components/Controls';
import Dialog from 'components/Dialog';
import AppBar from 'components/AppBar';
import BannerImage from 'components/BannerImage';
import PoweredBy from 'components/PoweredBy';

type Props = {
  onClose: () => void;
  onAsyncRegisterUser: (data:any, doRedirect?:boolean) => void;
  loading?: boolean;
}

const PostQuizQuestionDialog:FC<Props> = ({
  // Props
  onClose, onAsyncRegisterUser, loading = false,
}) => {
  const { t } = useTranslation('common');
  // State
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const postQuizQuestions:any = useAppSelector(selectPostQuizQuestions);

  const [ postQuizAnswers, setPostQuizAnswers ] = useState<any>(postQuizQuestions);

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;

    const [ questionRef, answerRef ] = name.split('.');

    setPostQuizAnswers((prevState:any) => {
      if ( typeof answerRef !== 'undefined' ){
        const question = prevState[questionRef];
        return {
          ...prevState,
          [questionRef]: {
            ...question,
            answers: Object.keys(question.answers).reduce((acc:any, key) => {
              const answer = question.answers[key];
              if ( question.type === QuestionTypes.SingleChoice ){
                acc[key] = { ...answer, checked: key === answerRef ? checked : false };
              } else {
                acc[key] = { ...answer, checked: key === answerRef ? checked : answer.checked };
              }
              return acc;
            }, {})
          }
        };
      }
      return {...prevState, [questionRef]: {
        ...prevState[questionRef],
        text: value
      }};
    })
  }

  const handleClick = async () => {
    if ( !quiz ) return;

    const result = [];
    for ( let key in postQuizAnswers ){
      const question = postQuizAnswers[key];
      const questionData = { ref: key, title: question.title };
      if ( question.type === QuestionTypes.ShortText ){
        result.push({
          ref: question.answerRef,
          text: question.text,
          question: questionData
        });
      } else {
        for ( let k in question.answers ){
          const answer = question.answers[k];
          if ( answer.checked ){
            result.push({
              ref: k,
              title: answer.title,
              question: questionData
            });
          }
        }
      }
    }

    onAsyncRegisterUser({
      registerData: null,
      resultData: {
        postQuizAnswers: result,
        nonce: quiz.nonce
      }
    }, true);
  }

  const isButtonDisabled = () => {
    let disabled = false;
    for ( let key in postQuizAnswers ){
      const question = postQuizAnswers[key];
      if ( question.type === QuestionTypes.ShortText ){
        if ( !question.text ) disabled = true;
      } else {
        const answers = question.answers;
        disabled = Object.keys(answers).every((k) => !answers[k].checked);
      }
      if ( disabled ) break;
    }
    return disabled;
  }

  return (
    <Dialog>
      <div className="flex flex-col w-full h-full bg-gray-200 overflow-y-auto">
        <AppBar  />
        <div className="flex-1 p-4">

          {Object.keys(postQuizAnswers).map((key) => {
            const question = postQuizAnswers[key];
            return (
              <div key={`post-quiz-question-${key}`} className="mb-8">
                <h2 className="text-lg mb-2">{question.title}</h2>
                {question.type === QuestionTypes.MultiChoice ? (
                  <span className="block text-gray-500 text-xs -mt-2 mb-2">
                    {`( ${t('quiz.questionPage.canBeMoreThan1Answer')} )`}
                  </span>
                ) : null}
                {question.type === QuestionTypes.ShortText ? (
                  <Input
                    name={key} id={key} placeholder="Free text"
                    value={question.text}
                    onChange={handleChange}
                  />
                ) : (
                  Object.keys(question.answers).map((k) => {
                    const answer = question.answers[k];
                    return (
                      <div key={`${key}-answer-${k}`}>
                        <Checkbox
                          className="mb-2"
                          size={4}
                          id={`${key}.${k}`}
                          name={`${key}.${k}`}
                          checked={answer.checked}
                          onChange={handleChange}
                        ><label htmlFor={`${key}.${k}`} className="text-md" >{answer.title}</label></Checkbox>
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}

          <div className="mb-4">
            <Button
              className="w-full hover:bg-none bg-primary hover:bg-primary text-white flex items-center justify-center gap-3"
              onClick={handleClick}
              disabled={isButtonDisabled()}
              loading={loading}
            >{t('common.submit')}</Button>
          </div>
        </div>
        {!quiz?.whiteLabeling && <PoweredBy />}
        <BannerImage />
      </div>
    </Dialog>
  )
}

export default PostQuizQuestionDialog;
